import {useSelector} from 'react-redux';
import {useCallback, useState} from 'react';
import {VpnKey} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {IStation} from '../../../../../dto/Station.dto';
import useSetStateAsBoolean from '../../../../../hooks/useBoolean.hook';
import GlobalDialog from '../../../../../components/dialogs/MessageOk';
import {apiGet} from '../../../../../axiosConfig';
import {companyIdSelector} from '../../../../../store/Slices/companySlice';
import DialogApiRequestStatus from '../../../../../components/dialogs/DialogApiRequestStatus';
import {CopyOnlyStringToClipBoard} from '../../../../../components/Button/CopyToClipboardButton';
import {useUserCanAny} from '../../../../../hooks/useUserCan';

/**
 * 🔐 GenerateStationPin Component
 *
 * Generates a temporary 5-digit OTP (PIN) for the given station.
 * The generated OTP is valid for 1 hour.
 * @param {{ station: IStation }} props - The station object for which to generate a PIN.
 *
 * @example
 * <GenerateStationPin station={station} />
 *
 * @returns {JSX.Element} A button that opens a dialog to generate and display the station PIN.
 */

const GenerateStationPin = ({station}: {station: IStation}) => {
  const companyId = useSelector(companyIdSelector);
  const [apiState, setApiState] = useState({
    status: '',
    error: null,
  });
  const isAdmin = useUserCanAny('is-admin,station:generate-qr');
  const {boolVal, setTrue, setFalse} = useSetStateAsBoolean(false);

  const [generatedPin, setGeneratedPin] = useState('');

  const closeLoadingDialog = useCallback(() => {
    setApiState({
      status: '',
      error: null,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setGeneratedPin('');
    setFalse();
  }, [setFalse]);

  const generatePin = useCallback(() => {
    if (!station.is_otp_generate && !isAdmin) {
      setTrue();
      return;
    }
    setApiState({
      status: 'pending',
      error: null,
    });
    apiGet(`v1/company/${companyId}/station/${station.id}/generate-pin`)
      .then(response => {
        const pinData = response?.data?.data;
        if (!pinData || isNaN(Number(pinData))) {
          setGeneratedPin('');
          throw new Error('Invalid PIN received from server');
        }
        setGeneratedPin(pinData);
        setApiState({
          status: '',
          error: null,
        });
        setTrue();
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  }, [companyId, isAdmin, setTrue, station.id, station.is_otp_generate]);

  return (
    <>
      <Tooltip
        title={!station.is_paid ? 'Payment Pending' : 'Generate Passcode'}>
        <span>
          <IconButton
            aria-label='pin-generate'
            onClick={generatePin}
            className='w-30 h-30 bg-dark radius-5'
            disabled={!station.is_paid}
            sx={{border: `1px solid var(--gray-variant14)`}}>
            <VpnKey />
          </IconButton>
        </span>
      </Tooltip>
      {boolVal && (
        <GlobalDialog
          dialogTitle={`Generate Passcode`}
          dialogContent={
            <Box>
              <Stack direction='row' spacing={2} mb={1}>
                <Typography variant='subtitle2' className='min-w-150'>
                  Station Name:
                </Typography>
                <Typography variant='body1' fontWeight={500}>
                  {station.name}
                </Typography>
              </Stack>

              <Stack direction='row' spacing={2} mb={1}>
                <Typography variant='subtitle2' className='min-w-150'>
                  Station Label:
                </Typography>
                <Typography variant='body1' fontWeight={500}>
                  {station.label || 'NA'}
                </Typography>
              </Stack>

              <Divider sx={{my: 1}} />

              {!station.is_otp_generate && !isAdmin ? (
                <Alert severity='warning' sx={{alignItems: 'center'}}>
                  You have already logged in.
                  <br /> Please contact your PSM to re-enable PIN generation.
                </Alert>
              ) : (
                <Stack direction='row' spacing={2} alignItems='flex-start'>
                  <Typography variant='subtitle2' className='min-w-150'>
                    Login Passcode:
                  </Typography>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <CopyOnlyStringToClipBoard
                      value={generatedPin.toString()}
                    />
                    <Typography fontSize={18} fontWeight={900}>
                      {generatedPin}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      (This OTP is valid for 60 minutes)
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Box>
          }
          customButtons={
            <Button onClick={closeDialog} variant='contained'>
              Close
            </Button>
          }
          onClose={setFalse}
        />
      )}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={generatePin}
          onEdit={closeLoadingDialog}
          onClose={closeLoadingDialog}
        />
      )}
    </>
  );
};

export default GenerateStationPin;
